import React, { useEffect, useRef, useState } from "react";
import Button from "../ui/button/Button";
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack5';
import Tooltip from "../ui/tooltip/Tooltip";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { base64ToBlob } from "../../utils/helpers/base64ToBlob";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;


type Props = {
    name: string;
    callbackOnAccept: () => void;
    descriptionTooltip: string | null;
    pdfBase64: string | null;
    titleTooltip: string;
}

const ModalPdfGeneralTerms: React.FC<Props> = ({
    name,
    callbackOnAccept,
    descriptionTooltip,
    pdfBase64,
    titleTooltip
}) => {
    const { t } = useTranslation();
    const mainContentRef = useRef<null | HTMLDivElement>(null);

    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<null | number>(null)
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfWidth, setPdfWidth] = useState<null | number>(null);

    const [hasAccepted, setHasAccepted] = useState<boolean>(false);

    useEffect(() => {
        if (!!pdfBase64) {
            const blobObj = base64ToBlob(pdfBase64, "pdf");
            if (blobObj.success && blobObj.file !== null) {
                const url = URL.createObjectURL(blobObj.file);
                setFileUrl(url);

                if (mainContentRef.current) {
                    const computedStyle = getComputedStyle(mainContentRef.current);
                    const paddingHorizontal = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
                    setPdfWidth(mainContentRef.current.offsetWidth - paddingHorizontal);
                }
            }
        }
    }, [pdfBase64]);

    const nextPage = () => {
        if (numPages && pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    }

    const prevPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    }

    const nameToId = React.useMemo(() => {
        return name.split(' ').join('_');
    }, [name]);

    const handleCallbackAccept = () => {
        if (hasAccepted === true) {
            callbackOnAccept();
        } else {
            toast.error(t('errors.errorModalPdfCheckbox'))
        }
    }

    return (
        <>
            {
                !!document &&
                <div className="fixed z[999] top-0 left-0 h-[100dvh] w-screen bg-[#00000080] flex items-center justify-center">
                    <div className="bg-white h-[95%] w-[95%] lg:w-[50%] max-w-[700px] flex flex-col">
                        <div className="shrink-0 border-b p-[30px] flex items-center ">
                            <div className="text-lg font-bold leading-[21.6px] mr-2">
                                {name}
                            </div>

                            <div className="relative">
                                <div className="cursor-pointer" id={`tooltip-generalTerms-trigger-${nameToId}`}>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z" fill="#CF0A2C" />
                                    </svg>
                                </div>

                                <Tooltip
                                    id={`tooltip-generalTerms-tooltip-${nameToId}`}
                                    triggerId={`tooltip-generalTerms-trigger-${nameToId}`}
                                    title={titleTooltip}
                                    description={descriptionTooltip}
                                    positionX="center"
                                    positionY="bottom"
                                    mobilePositionX="center"
                                    mobilePositionY="bottom"
                                />
                            </div>
                        </div>

                        {/* MAIN CONTENT */}
                        <div ref={mainContentRef} className="grow py-8 px-[30px] select-none overflow-auto relative">
                            {
                                fileUrl !== null && pdfWidth !== null &&
                                <>
                                    <Document file={fileUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                                        <Page width={pdfWidth} renderTextLayer={false} renderAnnotationLayer={false} pageNumber={pageNumber} />
                                    </Document>

                                    {
                                        !!pageNumber && !!numPages &&
                                        <div className="sticky flex items-center w-max rounded-[8px] bottom-0 left-[50%] -translate-x-[50%] z-10 bg-white shadow p-4">
                                            <div onClick={prevPage} className={"" + (pageNumber > 1 ? ' cursor-pointer' : ' opacity-50')}>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="text-sm mx-1 mb-[2px]">{pageNumber} / {numPages}</div>
                                            <div onClick={nextPage} className={"" + (numPages && pageNumber < numPages ? ' cursor-pointer' : ' opacity-50')}>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                                </svg>

                                            </div>
                                        </div>
                                    }

                                    <div>
                                        <a className="text-sm underline text-red" href={fileUrl} target="_blank">{t('components.modalGeneralTerms.viewFileFullPage')}</a>
                                    </div>
                                </>
                            }
                        </div>

                        <div className="select-none px-[30px] py-6 border-t flex items-center justify-between">
                            <div className="text-sm mr-2 flex items-center">
                                <input onChange={(e) => setHasAccepted(e.target.checked)} className="mr-1" id={nameToId + '-checkbox'} type="checkbox" />
                                <label className="mb-[3px]" htmlFor={nameToId + '-checkbox'}>{t('components.modalGeneralTerms.checkboxLabel')}</label>
                            </div>
                            <Button
                                callback={handleCallbackAccept}
                            >
                                {t('common.accept')}
                            </Button>
                        </div>

                    </div>
                </div>
            }
        </>
    );
}

export default ModalPdfGeneralTerms;